import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

const KEYWORD_PARAM = 'custom_keyword';
const PRICE_TYPE_PARAM = 'custom_price_type';
const COLLABORATION_PARAM = 'custom_collaboration';
const CATEGORY_PARAM = 'custom_category';
const FEATURED_ARTIST_PARAM = 'custom_featured_artist';
const SERIES_PARAM = 'custom_series';
const CHARACTER_PARAM = 'custom_character';
const SIZE_PARAM = 'custom_size';
const COLOR_PARAM = 'custom_color';
const SEX_PARAM = 'custom_sex';
const PAGE_PARAM = 'page';
const BASE_PAGE_SESSION_KEY = 'custom-collection-filters-base-page';
const FILTER_CHANGE_EVENT = 'custom-collection-filters:change';
const FILTER_RESULTS_EVENT = 'custom-collection-filters:results';
const COLLABORATION_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:collaboration-options-request';
const COLLABORATION_OPTIONS_EVENT = 'custom-collection-filters:collaboration-options';
const CATEGORY_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:category-options-request';
const CATEGORY_OPTIONS_EVENT = 'custom-collection-filters:category-options';
const FEATURED_ARTIST_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:featured-artist-options-request';
const FEATURED_ARTIST_OPTIONS_EVENT = 'custom-collection-filters:featured-artist-options';
const SERIES_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:series-options-request';
const SERIES_OPTIONS_EVENT = 'custom-collection-filters:series-options';
const CHARACTER_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:character-options-request';
const CHARACTER_OPTIONS_EVENT = 'custom-collection-filters:character-options';
const SIZE_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:size-options-request';
const SIZE_OPTIONS_EVENT = 'custom-collection-filters:size-options';
const COLOR_OPTIONS_EVENT = 'custom-collection-filters:color-options';
const SEX_OPTIONS_REQUEST_EVENT = 'custom-collection-filters:sex-options-request';
const SEX_OPTIONS_EVENT = 'custom-collection-filters:sex-options';
const FILTERED_PAGINATION_LABEL = 'Filtered results pagination';
const FILTERED_PAGINATION_PREVIOUS_LABEL = 'Previous page';
const FILTERED_PAGINATION_NEXT_LABEL = 'Next page';
const FILTERED_PAGINATION_PREVIOUS_ICON = `
  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M6.5 11L1.5 6L6.5 1" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
`;
const FILTERED_PAGINATION_NEXT_ICON = `
  <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
`;
const CHARACTER_OPTION_ORDER = [
  'nofuri',
  'threat red panda (ikaku)',
  'beautiful shadow',
  'rolling pandas',
  'shimaenaga',
  'control bear',
  'the four cats',
  'nagasugiru inu',
  'mendaco',
  'dangerous animals',
  'city boy penguin',
  'big mimi',
  'lalakoko',
  'icebonne',
  'shonen wolf',
  'lamb chop',
  'astro cat',
  'awesome tiger',
];
const SIZE_OPTION_ORDER = [
  'xxs',
  '2xs',
  'xs',
  'extra-small',
  'ss',
  's',
  'small',
  'm',
  'medium',
  'l',
  'large',
  'xl',
  'extra-large',
  'xxl',
  '2xl',
  '3xl',
  '4xl',
  '5xl',
  'free',
  'free-size',
  'one-size',
  'one-size-fits-all',
];

const SEX_OPTION_ORDER = ['unisex', 'women', 'kids'];
const SEX_OPTION_TITLES = {
  unisex: 'UNISEX',
  women: 'WOMEN',
  kids: 'KIDS',
};
const SEX_HIDDEN_COLLECTION_HANDLES = new Set(['goods', 'accessories']);
const SEX_FILTER_WRAPPER_SELECTOR = [
  '[data-custom-sex-filter]',
  '[data-filter-group="sex"]',
  '[data-filter-group="custom_sex"]',
  '[data-filter-name="sex"]',
  '[data-filter-name="custom_sex"]',
  '.custom-collection-filters__sex',
  '.custom-collection-filters__group',
  '.custom-collection-filters__filter',
  '.custom-collection-filters__section',
  '.facets__disclosure',
  'fieldset',
  'details',
].join(',');

const DEFERRED_FILTER_INPUT_NAMES = new Set([
  'custom_collaboration',
  'custom_category',
  'custom_featured_artist',
  'custom_series',
  'custom_character',
  'custom_size',
]);

const OPTION_FILTERS = {
  collaboration: {
    param: COLLABORATION_PARAM,
    inputName: 'custom_collaboration',
    triggerRef: 'collaborationTrigger',
    clearRef: 'collaborationClear',
    selectRef: 'collaborationSelect',
    optionsRef: 'collaborationOptions',
    summaryRef: 'collaborationSummary',
    requestEvent: COLLABORATION_OPTIONS_REQUEST_EVENT,
    optionsEvent: COLLABORATION_OPTIONS_EVENT,
    emptyLabel: 'No collaborations found.',
    stateKey: 'collaborations',
    cardKey: 'collaborations',
  },
  category: {
    param: CATEGORY_PARAM,
    inputName: 'custom_category',
    triggerRef: 'categoryTrigger',
    clearRef: 'categoryClear',
    selectRef: 'categorySelect',
    optionsRef: 'categoryOptions',
    summaryRef: 'categorySummary',
    requestEvent: CATEGORY_OPTIONS_REQUEST_EVENT,
    optionsEvent: CATEGORY_OPTIONS_EVENT,
    emptyLabel: 'No categories found.',
    stateKey: 'categories',
    cardKey: 'categories',
  },
  featuredArtist: {
    param: FEATURED_ARTIST_PARAM,
    inputName: 'custom_featured_artist',
    triggerRef: 'featuredArtistTrigger',
    clearRef: 'featuredArtistClear',
    selectRef: 'featuredArtistSelect',
    optionsRef: 'featuredArtistOptions',
    summaryRef: 'featuredArtistSummary',
    requestEvent: FEATURED_ARTIST_OPTIONS_REQUEST_EVENT,
    optionsEvent: FEATURED_ARTIST_OPTIONS_EVENT,
    emptyLabel: 'No featured artists found.',
    stateKey: 'featuredArtists',
    cardKey: 'featuredArtists',
  },
  series: {
    param: SERIES_PARAM,
    inputName: 'custom_series',
    triggerRef: 'seriesTrigger',
    clearRef: 'seriesClear',
    selectRef: 'seriesSelect',
    optionsRef: 'seriesOptions',
    summaryRef: 'seriesSummary',
    requestEvent: SERIES_OPTIONS_REQUEST_EVENT,
    optionsEvent: SERIES_OPTIONS_EVENT,
    emptyLabel: 'No series found.',
    stateKey: 'series',
    cardKey: 'series',
  },
  character: {
    param: CHARACTER_PARAM,
    inputName: 'custom_character',
    triggerRef: 'characterTrigger',
    clearRef: 'characterClear',
    selectRef: 'characterSelect',
    optionsRef: 'characterOptions',
    summaryRef: 'characterSummary',
    requestEvent: CHARACTER_OPTIONS_REQUEST_EVENT,
    optionsEvent: CHARACTER_OPTIONS_EVENT,
    emptyLabel: 'No characters found.',
    stateKey: 'characters',
    cardKey: 'characters',
  },
  size: {
    param: SIZE_PARAM,
    inputName: 'custom_size',
    triggerRef: 'sizeTrigger',
    clearRef: 'sizeClear',
    selectRef: 'sizeSelect',
    optionsRef: 'sizeOptions',
    summaryRef: 'sizeSummary',
    requestEvent: SIZE_OPTIONS_REQUEST_EVENT,
    optionsEvent: SIZE_OPTIONS_EVENT,
    emptyLabel: 'No sizes found.',
    stateKey: 'sizes',
    cardKey: 'sizes',
  },
  color: {
    param: COLOR_PARAM,
    inputName: 'custom_color',
    clearRef: 'colorClear',
    optionsRef: 'colorOptions',
    summaryRef: null,
    optionsEvent: COLOR_OPTIONS_EVENT,
    emptyLabel: 'No colors found.',
    stateKey: 'colors',
    cardKey: 'colors',
  },
  sex: {
    param: SEX_PARAM,
    inputName: 'custom_sex',
    optionsRef: 'sexOptions',
    summaryRef: null,
    requestEvent: SEX_OPTIONS_REQUEST_EVENT,
    optionsEvent: SEX_OPTIONS_EVENT,
    emptyLabel: 'No options found.',
    stateKey: 'sexes',
    cardKey: 'sexes',
  },
};

const CUSTOM_FILTER_DIALOG_SELECTOR = 'custom-collection-filters-form dialog.custom-collection-filters__modal';
const CUSTOM_FILTER_MODAL_TRIGGER_SELECTOR = Object.values(OPTION_FILTERS)
  .map((optionFilter) => optionFilter.triggerRef)
  .filter(Boolean)
  .map((triggerRef) => `[ref="${triggerRef}"]`)
  .join(',');

function closeCustomCollectionFilterDialogs({ exceptDialog = null } = {}) {
  document.querySelectorAll(CUSTOM_FILTER_DIALOG_SELECTOR).forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog === exceptDialog) return;

    try {
      if (dialog.open) dialog.close();
    } catch {
      dialog.removeAttribute('open');
    }

    dialog.removeAttribute('open');
    dialog.classList.remove('dialog-closing', 'is-open', 'active');
  });
}

function getCustomCollectionFilterDialogFromTrigger(trigger) {
  if (!(trigger instanceof Element)) return null;

  const dialogComponent = trigger.closest('dialog-component');
  const dialog = dialogComponent?.querySelector('dialog.custom-collection-filters__modal');

  return dialog instanceof HTMLDialogElement ? dialog : null;
}

function getOpenedCustomCollectionFilterDialogs() {
  return Array.from(document.querySelectorAll(CUSTOM_FILTER_DIALOG_SELECTOR)).filter(
    (dialog) => dialog instanceof HTMLDialogElement && dialog.open
  );
}

function syncCustomCollectionFilterModalState() {
  const hasOpenDialog = getOpenedCustomCollectionFilterDialogs().length > 0;
  document.documentElement.classList.toggle('custom-collection-filter-modal-open', hasOpenDialog);
}

function closeCustomCollectionFilterDialog(dialog) {
  if (!(dialog instanceof HTMLDialogElement)) return;

  try {
    if (dialog.open) dialog.close();
  } catch {
    dialog.removeAttribute('open');
  }

  dialog.removeAttribute('open');
  dialog.classList.remove('dialog-closing', 'is-open', 'active');
  syncCustomCollectionFilterModalState();
}

function getFilterStateFromUrl() {
  const url = new URL(window.location.href);

  return {
    keyword: (url.searchParams.get(KEYWORD_PARAM) || '').trim(),
    priceType: url.searchParams.get(PRICE_TYPE_PARAM) || 'all',
    collaborations: url.searchParams.getAll(COLLABORATION_PARAM),
    categories: url.searchParams.getAll(CATEGORY_PARAM),
    featuredArtists: url.searchParams.getAll(FEATURED_ARTIST_PARAM),
    series: url.searchParams.getAll(SERIES_PARAM),
    characters: url.searchParams.getAll(CHARACTER_PARAM),
    sizes: url.searchParams.getAll(SIZE_PARAM),
    colors: url.searchParams.getAll(COLOR_PARAM),
    sexes: url.searchParams.getAll(SEX_PARAM).filter((sexHandle) => SEX_OPTION_ORDER.includes(sexHandle)),
  };
}

function getCurrentPageFromUrl() {
  const url = new URL(window.location.href);
  const page = Number.parseInt(url.searchParams.get(PAGE_PARAM) || '1', 10);

  return Number.isFinite(page) && page > 0 ? page : 1;
}

function hasActiveCustomFilterState({
  keyword = '',
  priceType = 'all',
  collaborations = [],
  categories = [],
  featuredArtists = [],
  series = [],
  characters = [],
  sizes = [],
  colors = [],
  sexes = [],
}) {
  return (
    Boolean((keyword || '').trim()) ||
    (priceType || 'all') !== 'all' ||
    Boolean(collaborations?.length) ||
    Boolean(categories?.length) ||
    Boolean(featuredArtists?.length) ||
    Boolean(series?.length) ||
    Boolean(characters?.length) ||
    Boolean(sizes?.length) ||
    Boolean(colors?.length) ||
    Boolean(sexes?.length)
  );
}

function updateFilterStateInUrl({
  keyword,
  priceType,
  collaborations = [],
  categories = [],
  featuredArtists = [],
  series = [],
  characters = [],
  sizes = [],
  colors = [],
  sexes = [],
}) {
  const url = new URL(window.location.href);
  const previousState = getFilterStateFromUrl();
  const hadActiveFilters = hasActiveCustomFilterState(previousState);

  if (keyword) {
    url.searchParams.set(KEYWORD_PARAM, keyword);
  } else {
    url.searchParams.delete(KEYWORD_PARAM);
  }

  if (priceType && priceType !== 'all') {
    url.searchParams.set(PRICE_TYPE_PARAM, priceType);
  } else {
    url.searchParams.delete(PRICE_TYPE_PARAM);
  }

  url.searchParams.delete(COLLABORATION_PARAM);
  collaborations.forEach((collaboration) => {
    if (collaboration) url.searchParams.append(COLLABORATION_PARAM, collaboration);
  });

  url.searchParams.delete(CATEGORY_PARAM);
  categories.forEach((category) => {
    if (category) url.searchParams.append(CATEGORY_PARAM, category);
  });

  url.searchParams.delete(FEATURED_ARTIST_PARAM);
  featuredArtists.forEach((artist) => {
    if (artist) url.searchParams.append(FEATURED_ARTIST_PARAM, artist);
  });

  url.searchParams.delete(SERIES_PARAM);
  series.forEach((seriesHandle) => {
    if (seriesHandle) url.searchParams.append(SERIES_PARAM, seriesHandle);
  });

  url.searchParams.delete(CHARACTER_PARAM);
  characters.forEach((characterHandle) => {
    if (characterHandle) url.searchParams.append(CHARACTER_PARAM, characterHandle);
  });

  url.searchParams.delete(SIZE_PARAM);
  sizes.forEach((sizeHandle) => {
    if (sizeHandle) url.searchParams.append(SIZE_PARAM, sizeHandle);
  });

  url.searchParams.delete(COLOR_PARAM);
  colors.forEach((colorHandle) => {
    if (colorHandle) url.searchParams.append(COLOR_PARAM, colorHandle);
  });

  url.searchParams.delete(SEX_PARAM);
  sexes
    .filter((sexHandle) => SEX_OPTION_ORDER.includes(sexHandle))
    .forEach((sexHandle) => {
      if (sexHandle) url.searchParams.append(SEX_PARAM, sexHandle);
    });

  const shouldResetPagination = hasActiveCustomFilterState({
    keyword,
    priceType,
    collaborations,
    categories,
    featuredArtists,
    series,
    characters,
    sizes,
    colors,
    sexes,
  });

  try {
    if (shouldResetPagination) {
      if (!hadActiveFilters) {
        const currentPage = url.searchParams.get(PAGE_PARAM) || '';
        if (currentPage) {
          sessionStorage.setItem(BASE_PAGE_SESSION_KEY, currentPage);
        }
      }
      url.searchParams.delete(PAGE_PARAM);
    } else {
      const storedPage = sessionStorage.getItem(BASE_PAGE_SESSION_KEY) || '';

      if (storedPage) {
        url.searchParams.set(PAGE_PARAM, storedPage);
      } else {
        url.searchParams.delete(PAGE_PARAM);
      }

      sessionStorage.removeItem(BASE_PAGE_SESSION_KEY);
    }
  } catch {
    if (shouldResetPagination) {
      url.searchParams.delete(PAGE_PARAM);
    }
  }

  history.replaceState({}, '', url.toString());
}

function updateActiveFilterPageInUrl(page, { replace = true } = {}) {
  const url = new URL(window.location.href);
  const currentState = getFilterStateFromUrl();

  if (!hasActiveCustomFilterState(currentState)) return;

  if (page > 1) {
    url.searchParams.set(PAGE_PARAM, String(page));
  } else {
    url.searchParams.delete(PAGE_PARAM);
  }

  if (replace) {
    history.replaceState({}, '', url.toString());
    return;
  }

  history.pushState({}, '', url.toString());
}

class CustomCollectionFiltersForm extends Component {
  #debounceTimeout;
  #previewTimeout;
  #previewRequestId = 0;
  #syncing = false;
  #options = {
    collaboration: [],
    category: [],
    featuredArtist: [],
    series: [],
    character: [],
    size: [],
    color: [],
    sex: [],
  };

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleInput);
    document.addEventListener('click', this.#handleClick);
    document.addEventListener(FILTER_CHANGE_EVENT, this.#handleExternalChange);
    document.addEventListener(FILTER_RESULTS_EVENT, this.#handleResultsUpdate);
    document.addEventListener(COLLABORATION_OPTIONS_EVENT, this.#handleCollaborationOptions);
    document.addEventListener(CATEGORY_OPTIONS_EVENT, this.#handleCategoryOptions);
    document.addEventListener(FEATURED_ARTIST_OPTIONS_EVENT, this.#handleFeaturedArtistOptions);
    document.addEventListener(SERIES_OPTIONS_EVENT, this.#handleSeriesOptions);
    document.addEventListener(CHARACTER_OPTIONS_EVENT, this.#handleCharacterOptions);
    document.addEventListener(SIZE_OPTIONS_EVENT, this.#handleSizeOptions);
    document.addEventListener(COLOR_OPTIONS_EVENT, this.#handleColorOptions);
    document.addEventListener(SEX_OPTIONS_EVENT, this.#handleSexOptions);

    this.#applyStateToInputs(getFilterStateFromUrl());
    this.#updateResultActionLabels(this.#getInitialResultsCount());
    this.#syncOptionFilterVisibility('sex', this.#options.sex);
    document.dispatchEvent(new CustomEvent(SEX_OPTIONS_REQUEST_EVENT));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    clearTimeout(this.#debounceTimeout);
    clearTimeout(this.#previewTimeout);
    document.removeEventListener('click', this.#handleClick);
    document.removeEventListener(FILTER_CHANGE_EVENT, this.#handleExternalChange);
    document.removeEventListener(FILTER_RESULTS_EVENT, this.#handleResultsUpdate);
    document.removeEventListener(COLLABORATION_OPTIONS_EVENT, this.#handleCollaborationOptions);
    document.removeEventListener(CATEGORY_OPTIONS_EVENT, this.#handleCategoryOptions);
    document.removeEventListener(FEATURED_ARTIST_OPTIONS_EVENT, this.#handleFeaturedArtistOptions);
    document.removeEventListener(SERIES_OPTIONS_EVENT, this.#handleSeriesOptions);
    document.removeEventListener(CHARACTER_OPTIONS_EVENT, this.#handleCharacterOptions);
    document.removeEventListener(SIZE_OPTIONS_EVENT, this.#handleSizeOptions);
    document.removeEventListener(COLOR_OPTIONS_EVENT, this.#handleColorOptions);
    document.removeEventListener(SEX_OPTIONS_EVENT, this.#handleSexOptions);
  }

  #handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.name.startsWith('custom_')) {
      event.stopPropagation();
    }

    clearTimeout(this.#debounceTimeout);
    clearTimeout(this.#previewTimeout);

    if (target.type === 'checkbox' && this.#isDeferredInput(target.name)) {
      this.#schedulePreviewUpdate(0);
      return;
    }

    const delay = target.type === 'search' ? 180 : target.type === 'checkbox' ? 80 : 0;
    this.#schedulePreviewUpdate(delay);
    this.#debounceTimeout = window.setTimeout(() => {
      if (this.#syncing) return;

      this.#dispatchStateChange();
    }, delay);
  };

  #handleExternalChange = (event) => {
    this.#resetModalSelectButtonsToAppliedCount();

    const {
      keyword = '',
      priceType = 'all',
      collaborations = [],
      categories = [],
      featuredArtists = [],
      series = [],
      characters = [],
      sizes = [],
      colors = [],
      sexes = [],
    } = event.detail || {};

    this.#applyStateToInputs({
      keyword,
      priceType,
      collaborations,
      categories,
      featuredArtists,
      series,
      characters,
      sizes,
      colors,
      sexes,
    });
  };

  #handleResultsUpdate = (event) => {
    const countText = this.querySelector('[ref="countText"]');
    const { count, loading } = event.detail || {};

    if (typeof count === 'number') {
      this.dataset.resultsCount = String(count);
      this.#invalidatePreviewUpdates();

      if (countText instanceof HTMLElement) {
        countText.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      }

      this.#updateResultActionLabels(count);
    }

    this.toggleAttribute('data-loading', Boolean(loading));
  };

  #schedulePreviewUpdate(delay) {
    this.#previewTimeout = window.setTimeout(() => {
      if (this.#syncing) return;

      void this.#updatePreviewResultsCount();
    }, delay);
  }

  async #updatePreviewResultsCount() {
    const resultsList = this.closest('results-list');
    if (!(resultsList instanceof HTMLElement)) return;

    const controller = resultsList.querySelector('custom-collection-filters-controller');
    if (!(controller instanceof HTMLElement) || typeof controller.getFilteredCountForState !== 'function') return;

    const requestId = ++this.#previewRequestId;

    try {
      const count = await controller.getFilteredCountForState(this.#getCurrentState());
      if (requestId !== this.#previewRequestId || typeof count !== 'number') return;

      this.#updateResultActionLabels(count);
    } catch {}
  }

  #getInitialResultsCount() {
    const count = Number(this.dataset.resultsCount || '0');
    return Number.isFinite(count) && count >= 0 ? count : 0;
  }

  #invalidatePreviewUpdates() {
    clearTimeout(this.#previewTimeout);
    this.#previewRequestId += 1;
  }

  #resetModalSelectButtonsToAppliedCount() {
    this.#invalidatePreviewUpdates();
    this.#updateResultActionLabels(this.#getInitialResultsCount());
  }

  #getSeeItemsLabel(count) {
    const template = count === 1 ? this.dataset.seeItemsOne : this.dataset.seeItemsOther;
    const fallback = count === 1 ? 'See [count] item' : 'See [count] items';

    return (template || fallback).replace('[count]', String(count));
  }

  #updateModalSelectButtons(count) {
    const label = this.#getSeeItemsLabel(count);

    this.querySelectorAll('.custom-collection-filters__modal-select').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      button.textContent = label;
      button.setAttribute('aria-label', label);
    });
  }

  #getSeeItemsButtonLabel(count) {
    const template = this.dataset.seeItemsButtonLabel || 'See Items ([count])';
    return template.replace('[count]', String(count));
  }

  #updateSeeItemsButton(count) {
    const button = this.querySelector('[ref="seeItemsButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const label = this.#getSeeItemsButtonLabel(count);
    button.textContent = label;
    button.setAttribute('aria-label', label);
  }

  #updateResultActionLabels(count) {
    this.#updateModalSelectButtons(count);
    this.#updateSeeItemsButton(count);
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const resultsList = this.closest('results-list');
    if (!(resultsList instanceof HTMLElement)) return;

    const removeButton = target.closest('[data-remove-filter]');
    if (removeButton instanceof HTMLButtonElement && resultsList.contains(removeButton)) {
      this.#removeSelectedFilter(removeButton);
      return;
    }

    const keywordSearchButton = target.closest('[ref="keywordSearchButton"]');
    if (keywordSearchButton instanceof HTMLButtonElement && this.contains(keywordSearchButton)) {
      clearTimeout(this.#debounceTimeout);
      this.#dispatchStateChange();
      return;
    }

    const seeItemsButton = target.closest('[ref="seeItemsButton"]');
    if (seeItemsButton instanceof HTMLButtonElement && this.contains(seeItemsButton)) {
      clearTimeout(this.#debounceTimeout);
      this.#dispatchStateChange();
      this.#scrollToResults();
      return;
    }

    for (const optionFilter of Object.values(OPTION_FILTERS)) {
      const trigger = optionFilter.triggerRef ? target.closest(`[ref="${optionFilter.triggerRef}"]`) : null;

      if (trigger instanceof Element && this.contains(trigger)) {
        const currentDialog = getCustomCollectionFilterDialogFromTrigger(trigger);

        // Prevent stacked filter popups when customers click another filter while a popup is open.
        closeCustomCollectionFilterDialogs({ exceptDialog: currentDialog });
        window.setTimeout(syncCustomCollectionFilterModalState, 0);

        this.#applyStateToInputs(getFilterStateFromUrl());
        this.#resetModalSelectButtonsToAppliedCount();
        document.dispatchEvent(new CustomEvent(optionFilter.requestEvent));
        return;
      }

      const clearButton = optionFilter.clearRef ? target.closest(`[ref="${optionFilter.clearRef}"]`) : null;

      if (clearButton instanceof Element && this.contains(clearButton)) {
        // Clear buttons should apply immediately, even for modal/deferred filters.
        // This keeps the filter cleared if the customer closes the popup with X/backdrop.
        this.#clearOptionSelections(optionFilter.inputName, false);
        return;
      }

      const selectButton = optionFilter.selectRef ? target.closest(`[ref="${optionFilter.selectRef}"]`) : null;

      if (selectButton instanceof Element && this.contains(selectButton)) {
        this.#dispatchStateChange();
        return;
      }
    }
  };

  #handleCollaborationOptions = (event) => {
    this.#handleOptionsEvent('collaboration', event);
  };

  #handleCategoryOptions = (event) => {
    this.#handleOptionsEvent('category', event);
  };

  #handleFeaturedArtistOptions = (event) => {
    this.#handleOptionsEvent('featuredArtist', event);
  };

  #handleSeriesOptions = (event) => {
    this.#handleOptionsEvent('series', event);
  };

  #handleCharacterOptions = (event) => {
    this.#handleOptionsEvent('character', event);
  };

  #handleSizeOptions = (event) => {
    this.#handleOptionsEvent('size', event);
  };

  #handleColorOptions = (event) => {
    this.#handleOptionsEvent('color', event);
  };

  #handleSexOptions = (event) => {
    this.#handleOptionsEvent('sex', event);
  };

  #getCurrentState() {
    const keywordInput = this.querySelector('[ref="keywordInput"]');
    const checkedPriceType = this.querySelector('input[type="radio"]:checked');
    const selectedOptions = {};

    Object.values(OPTION_FILTERS).forEach((optionFilter) => {
      selectedOptions[optionFilter.stateKey] = Array.from(
        this.querySelectorAll(`input[name="${optionFilter.inputName}"]:checked`)
      )
        .filter((input) => input instanceof HTMLInputElement)
        .map((input) => input.value);
    });

    return {
      keyword: keywordInput instanceof HTMLInputElement ? keywordInput.value.trim() : '',
      priceType: checkedPriceType instanceof HTMLInputElement ? checkedPriceType.value : 'all',
      ...selectedOptions,
    };
  }

  #applyStateToInputs({
    keyword = '',
    priceType = 'all',
    collaborations = [],
    categories = [],
    featuredArtists = [],
    series = [],
    characters = [],
    sizes = [],
    colors = [],
    sexes = [],
  }) {
    this.#syncing = true;

    const keywordInput = this.querySelector('[ref="keywordInput"]');

    if (keywordInput instanceof HTMLInputElement) {
      keywordInput.value = keyword;
    }

    const radioInputs = this.querySelectorAll('input[type="radio"]');

    radioInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = input.value === priceType;
      }
    });

    const stateSelections = {
      collaborations,
      categories,
      featuredArtists,
      series,
      characters,
      sizes,
      colors,
      sexes,
    };

    Object.values(OPTION_FILTERS).forEach((optionFilter) => {
      const selectedValues = new Set(stateSelections[optionFilter.stateKey] || []);
      const inputs = this.querySelectorAll(`input[name="${optionFilter.inputName}"]`);

      inputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = selectedValues.has(input.value);
        }
      });

      if (optionFilter.summaryRef) {
        this.#updateSelectionSummary(optionFilter.summaryRef, selectedValues.size);
      }
    });

    this.#syncing = false;
    this.#renderSelectedFilters();
  }

  #clearOptionSelections(inputName, deferApply = false) {
    const inputs = this.querySelectorAll(`input[name="${inputName}"]`);

    inputs.forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = false;
    });

    const state = this.#getCurrentState();

    // Apply Clear immediately for popup filters. Previously the deferred popup filters
    // only updated the preview count, so closing the popup restored the old URL filter.
    if (!deferApply) {
      this.#dispatchStateChange(state);
      return;
    }

    this.#applyStateToInputs(state);
    this.#schedulePreviewUpdate(0);
  }

  #handleOptionsEvent(type, event) {
    const options = event.detail?.options;
    if (!Array.isArray(options)) return;

    this.#options[type] = options;
    this.#syncOptionFilterVisibility(type, options);
    this.#renderOptionChoices(type);
    this.#applyStateToInputs(getFilterStateFromUrl());
    this.#resetModalSelectButtonsToAppliedCount();
  }

  #syncOptionFilterVisibility(type, options = []) {
    if (type !== 'sex') return;

    const optionFilter = OPTION_FILTERS.sex;
    const optionsContainer = this.querySelector(`[ref="${optionFilter.optionsRef}"]`);
    if (!(optionsContainer instanceof HTMLElement)) return;

    const shouldHide = !Array.isArray(options) || options.length === 0;
    const wrapper =
      optionsContainer.closest(SEX_FILTER_WRAPPER_SELECTOR) ||
      optionsContainer.closest(`[ref="${optionFilter.optionsRef}"]`) ||
      optionsContainer.parentElement;

    if (wrapper instanceof HTMLElement) {
      wrapper.hidden = shouldHide;
      wrapper.toggleAttribute('data-custom-sex-filter-hidden', shouldHide);
    }

    if (shouldHide) {
      this.#clearHiddenSexSelections();
    }
  }

  #clearHiddenSexSelections() {
    const optionFilter = OPTION_FILTERS.sex;
    const checkedInputs = Array.from(this.querySelectorAll(`input[name="${optionFilter.inputName}"]:checked`)).filter(
      (input) => input instanceof HTMLInputElement
    );

    checkedInputs.forEach((input) => {
      input.checked = false;
    });

    const currentUrlState = getFilterStateFromUrl();

    if (currentUrlState.sexes.length) {
      const state = this.#getCurrentState();
      state.sexes = [];
      this.#dispatchStateChange(state);
    }
  }

  #renderSelectedFilters() {
    const containers = this.#getSelectedFilterContainers();
    if (!containers.length) return;

    const state = this.#getCurrentState();
    const chips = [];
    const optionTitles = this.#getOptionTitleMaps();

    if (state.keyword) {
      chips.push({
        label: state.keyword,
        type: 'keyword',
      });
    }

    if (state.priceType && state.priceType !== 'all') {
      chips.push({
        label: state.priceType === 'sale' ? 'Sale price' : 'Regular price',
        type: 'priceType',
      });
    }

    Object.values(OPTION_FILTERS).forEach((optionFilter) => {
      const values = state[optionFilter.stateKey] || [];

      values.forEach((value) => {
        const title = optionTitles[optionFilter.stateKey]?.get(value) || value;

        chips.push({
          label: title,
          type: optionFilter.stateKey,
          value,
        });
      });
    });

    if (!chips.length) {
      containers.forEach((container) => {
        container.hidden = true;
        container.innerHTML = '';
      });

      return;
    }

    const markup = chips
      .map((chip) => {
        const valueAttribute = chip.value ? ` data-filter-value="${this.#escapeAttribute(chip.value)}"` : '';

        return `
          <button
            type="button"
            class="custom-collection-filters__selected-pill"
            data-remove-filter="${this.#escapeAttribute(chip.type)}"${valueAttribute}
            aria-label="Remove ${this.#escapeAttribute(chip.label)} filter"
          >
            <span class="custom-collection-filters__selected-pill-label">${this.#escapeHTML(chip.label)}</span>
            <span class="custom-collection-filters__selected-pill-close" aria-hidden="true">&times;</span>
          </button>
        `;
      })
      .join('');

    containers.forEach((container) => {
      container.hidden = false;
      container.innerHTML = markup;
    });
  }

  #getSelectedFilterContainers() {
    const containers = [];
    const resultsList = this.closest('results-list');

    if (!(resultsList instanceof HTMLElement)) {
      return containers;
    }

    this.#injectSelectedFilterBarStyles();

    containers.push(...resultsList.querySelectorAll('[ref="customSelectedFiltersBar"]'));

    let mainContainer = containers.find((container) => {
      if (!(container instanceof HTMLElement)) return false;
      return !container.closest('[data-custom-mobile-filter-drawer]');
    });

    if (!(mainContainer instanceof HTMLElement)) {
      const grid =
        resultsList.querySelector('#ResultsList') ||
        resultsList.querySelector('[ref="grid"]') ||
        resultsList.querySelector('.main-collection-grid') ||
        resultsList.querySelector('.product-grid');

      if (grid instanceof HTMLElement && grid.parentElement instanceof HTMLElement) {
        mainContainer = document.createElement('div');
        mainContainer.className = 'custom-collection-filters-selected-bar';
        mainContainer.setAttribute('ref', 'customSelectedFiltersBar');
        mainContainer.setAttribute('aria-live', 'polite');
        mainContainer.setAttribute('data-custom-filter-selected-bar-auto', '');
        mainContainer.hidden = true;

        grid.parentElement.insertBefore(mainContainer, grid);
        containers.push(mainContainer);
      }
    }

    return containers.filter((container) => container instanceof HTMLElement);
  }

  #injectSelectedFilterBarStyles() {
    if (document.getElementById('custom-collection-filters-selected-bar-styles')) return;

    const style = document.createElement('style');
    style.id = 'custom-collection-filters-selected-bar-styles';
    style.textContent = `
      .custom-collection-filters-selected-bar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-start;
        gap: 0.5rem;
        width: 100%;
        margin-bottom: 0.9rem;
      }

      .custom-collection-filters-selected-bar[hidden] {
        display: none !important;
      }

      .collection-body--with-custom-filters > .collection-products-area > .custom-collection-filters-selected-bar,
      .collection-products-area > .custom-collection-filters-selected-bar {
        width: 100%;
        margin: 0 0 0.9rem;
      }

      .custom-collection-filters__selected-pill {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.45rem;
        min-height: 2rem;
        padding: 0.35rem 0.75rem;
        border: 1px solid rgb(var(--color-foreground-rgb) / 0.16);
        border-radius: 999px;
        background: rgb(var(--color-background-rgb, 255 255 255));
        color: rgb(var(--color-foreground-rgb));
        font: inherit;
        font-size: 0.82rem;
        line-height: 1;
        cursor: pointer;
        text-decoration: none;
      }

      .custom-collection-filters__selected-pill:hover {
        background: rgb(var(--color-foreground-rgb) / 0.05);
      }

      .custom-collection-filters__selected-pill:focus-visible {
        outline: 2px solid rgb(var(--color-foreground-rgb));
        outline-offset: 2px;
      }

      .custom-collection-filters__selected-pill-label {
        display: inline-block;
      }

      .custom-collection-filters__selected-pill-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        line-height: 1;
      }
    `;

    document.head.appendChild(style);
  }

  #getOptionTitleMaps() {
    return {
      collaborations: new Map((this.#options.collaboration || []).map((option) => [option.handle, option.title])),
      categories: new Map((this.#options.category || []).map((option) => [option.handle, option.title])),
      featuredArtists: new Map((this.#options.featuredArtist || []).map((option) => [option.handle, option.title])),
      series: new Map((this.#options.series || []).map((option) => [option.handle, option.title])),
      characters: new Map((this.#options.character || []).map((option) => [option.handle, option.title])),
      sizes: new Map((this.#options.size || []).map((option) => [option.handle, option.title])),
      colors: new Map((this.#options.color || []).map((option) => [option.handle, option.title])),
      sexes: new Map((this.#options.sex || []).map((option) => [option.handle, option.title])),
    };
  }

  #removeSelectedFilter(button) {
    const type = button.dataset.removeFilter;
    const value = button.dataset.filterValue || '';

    if (!type) return;

    const state = this.#getCurrentState();

    if (type === 'keyword') {
      state.keyword = '';
    } else if (type === 'priceType') {
      state.priceType = 'all';
    } else {
      const optionFilter = Object.values(OPTION_FILTERS).find(
        (item) => item.stateKey === type && Array.isArray(state[type])
      );

      if (!optionFilter) return;

      state[type] = state[type].filter((item) => item !== value);
    }

    this.#applyStateToInputs(state);
    this.#dispatchStateChange(state);
  }

  #dispatchStateChange(state = this.#getCurrentState()) {
    clearTimeout(this.#debounceTimeout);
    this.#invalidatePreviewUpdates();
    updateFilterStateInUrl(state);

    document.dispatchEvent(
      new CustomEvent(FILTER_CHANGE_EVENT, {
        detail: state,
      })
    );
  }

  #scrollToResults() {
    const resultsList = this.closest('results-list');
    if (!(resultsList instanceof HTMLElement)) return;

    const target = resultsList.querySelector('#ResultsList, [ref="grid"]');
    if (!(target instanceof HTMLElement)) return;

    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  #isDeferredInput(inputName) {
    return DEFERRED_FILTER_INPUT_NAMES.has(inputName);
  }

  #renderOptionChoices(type) {
    const optionFilter = OPTION_FILTERS[type];
    if (!optionFilter) return;

    const container = this.querySelector(`[ref="${optionFilter.optionsRef}"]`);
    if (!(container instanceof HTMLElement)) return;

    const options = this.#options[type] || [];

    if (!options.length) {
      container.innerHTML = `<p class="custom-collection-filters__loading">${optionFilter.emptyLabel}</p>`;
      return;
    }

    if (type === 'color') {
      container.innerHTML = options
        .map(
          (option) => `
            <label class="custom-collection-filters__color-option" title="${this.#escapeAttribute(option.title)}">
              <input
                type="checkbox"
                name="${optionFilter.inputName}"
                value="${this.#escapeAttribute(option.handle)}"
              >
              <span
                class="custom-collection-filters__color-swatch"
                style="--custom-filter-swatch: ${this.#escapeAttribute(option.swatch || option.handle)};"
                aria-hidden="true"
              ></span>
              <span class="visually-hidden">${this.#escapeHTML(option.title)}</span>
            </label>
          `
        )
        .join('');
      return;
    }

    if (type === 'sex') {
      container.innerHTML = options
        .map(
          (option) => `
            <label class="custom-collection-filters__option">
              <input
                type="checkbox"
                name="${optionFilter.inputName}"
                value="${this.#escapeAttribute(option.handle)}"
              >
              <span>${this.#escapeHTML(option.title)}</span>
            </label>
          `
        )
        .join('');
      return;
    }

    container.innerHTML = options
      .map(
        (option) => `
          <label class="custom-collection-filters__collaboration-option">
            <input
              type="checkbox"
              name="${optionFilter.inputName}"
              value="${this.#escapeAttribute(option.handle)}"
            >
            <span>${this.#escapeHTML(option.title)}</span>
          </label>
        `
      )
      .join('');
  }

  #updateSelectionSummary(summaryRef, count) {
    const summary = this.querySelector(`[ref="${summaryRef}"]`);
    if (!(summary instanceof HTMLElement)) return;

    summary.textContent = count > 0 ? `${count} selected` : 'Select';
  }

  #escapeHTML(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  #escapeAttribute(value) {
    return this.#escapeHTML(value).replace(/"/g, '&quot;');
  }
}

class CustomCollectionFiltersController extends Component {
  #allCards = null;
  #allCardsPromise = null;
  #baseGridHTML = '';
  #baseLastPage = '1';
  #baseUrlKey = '';
  #filteredCards = [];
  #filteredCurrentPage = 1;
  #filteredStateKey = '';
  #isCustomFilteringActive = false;
  #idlePrefetchScheduled = false;
  #optionCollectionsHydrated = false;
  #requestId = 0;
  #optionCollections = {
    collaboration: [],
    category: [],
    featuredArtist: [],
    series: [],
    character: [],
    size: [],
    color: [],
    sex: [],
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(FILTER_CHANGE_EVENT, this.#handleFilterChange);
    document.addEventListener(COLLABORATION_OPTIONS_REQUEST_EVENT, this.#handleCollaborationOptionsRequest);
    document.addEventListener(CATEGORY_OPTIONS_REQUEST_EVENT, this.#handleCategoryOptionsRequest);
    document.addEventListener(FEATURED_ARTIST_OPTIONS_REQUEST_EVENT, this.#handleFeaturedArtistOptionsRequest);
    document.addEventListener(SERIES_OPTIONS_REQUEST_EVENT, this.#handleSeriesOptionsRequest);
    document.addEventListener(CHARACTER_OPTIONS_REQUEST_EVENT, this.#handleCharacterOptionsRequest);
    document.addEventListener(SIZE_OPTIONS_REQUEST_EVENT, this.#handleSizeOptionsRequest);
    document.addEventListener(SEX_OPTIONS_REQUEST_EVENT, this.#handleSexOptionsRequest);
    document.addEventListener('click', this.#handlePaginationClick);

    this.#captureBaseState();
    this.#removeHiddenSexFilterFromUrl();
    this.#seedColorOptions();
    this.#seedSexOptions();
    this.#scheduleIdlePrefetch();

    const state = getFilterStateFromUrl();

    if (this.#isActiveState(state)) {
      this.#ensureBasePageStored();

      queueMicrotask(() => {
        this.#applyFilters(state);
      });
    } else {
      this.#broadcastResults({
        count: this.#getBaseCount(),
        loading: false,
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(FILTER_CHANGE_EVENT, this.#handleFilterChange);
    document.removeEventListener(COLLABORATION_OPTIONS_REQUEST_EVENT, this.#handleCollaborationOptionsRequest);
    document.removeEventListener(CATEGORY_OPTIONS_REQUEST_EVENT, this.#handleCategoryOptionsRequest);
    document.removeEventListener(FEATURED_ARTIST_OPTIONS_REQUEST_EVENT, this.#handleFeaturedArtistOptionsRequest);
    document.removeEventListener(SERIES_OPTIONS_REQUEST_EVENT, this.#handleSeriesOptionsRequest);
    document.removeEventListener(CHARACTER_OPTIONS_REQUEST_EVENT, this.#handleCharacterOptionsRequest);
    document.removeEventListener(SIZE_OPTIONS_REQUEST_EVENT, this.#handleSizeOptionsRequest);
    document.removeEventListener(SEX_OPTIONS_REQUEST_EVENT, this.#handleSexOptionsRequest);
    document.removeEventListener('click', this.#handlePaginationClick);
  }

  #seedColorOptions() {
    const visibleCards = this.#getVisibleCards();

    if (visibleCards.length) {
      this.#optionCollections.color = this.#getOptionsFromCards(visibleCards, 'colors');
      this.#broadcastOptions('color', this.#optionCollections.color);
    }
  }

  #seedSexOptions() {
    if (this.shouldHideSexFilter) {
      this.#optionCollections.sex = [];
      this.#broadcastOptions('sex', []);
      return;
    }

    const visibleCards = this.#getVisibleCards();

    if (visibleCards.length) {
      this.#optionCollections.sex = this.#getOptionsFromCards(visibleCards, 'sexes');
      this.#broadcastOptions('sex', this.#optionCollections.sex);
    }
  }

  get sectionId() {
    const id = this.getAttribute('section-id') || this.closest('results-list')?.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required for custom collection filters');

    return id;
  }

  get collectionSexHandle() {
    const sexHandle = (this.getAttribute('collection-sex') || '').trim().toLowerCase();
    return SEX_OPTION_ORDER.includes(sexHandle) ? sexHandle : '';
  }

  get collectionHandle() {
    const attributeHandle = (this.getAttribute('collection-handle') || '').trim().toLowerCase();

    if (attributeHandle) {
      return attributeHandle;
    }

    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match?.[1] ? decodeURIComponent(match[1]).trim().toLowerCase() : '';
  }

  get shouldHideSexFilter() {
    const explicitValue = (this.getAttribute('hide-sex-filter') || '').trim().toLowerCase();

    if (['true', '1', 'yes'].includes(explicitValue)) {
      return true;
    }

    return SEX_HIDDEN_COLLECTION_HANDLES.has(this.collectionHandle);
  }

  get pageSize() {
    const pageSize = Number.parseInt(this.getAttribute('page-size') || '24', 10);
    return Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 24;
  }

  async getFilteredCountForState(state = getFilterStateFromUrl()) {
    const normalizedState = this.#normalizeState(state);

    if (!this.#isActiveState(normalizedState)) {
      return this.#getBaseCount();
    }

    const cards = await this.#getAllCards();

    return this.#getFilteredCards(cards, normalizedState).length;
  }

  #handleFilterChange = async (event) => {
    await this.#applyFilters(event.detail || getFilterStateFromUrl());
  };

  #handleCollaborationOptionsRequest = async () => {
    await this.#handleOptionRequest('collaboration');
  };

  #handleCategoryOptionsRequest = async () => {
    await this.#handleOptionRequest('category');
  };

  #handleFeaturedArtistOptionsRequest = async () => {
    await this.#handleOptionRequest('featuredArtist');
  };

  #handleSeriesOptionsRequest = async () => {
    await this.#handleOptionRequest('series');
  };

  #handleCharacterOptionsRequest = async () => {
    await this.#handleOptionRequest('character');
  };

  #handleSizeOptionsRequest = async () => {
    await this.#handleOptionRequest('size');
  };

  #handleSexOptionsRequest = async () => {
    await this.#handleOptionRequest('sex');
  };

  #handlePaginationClick = (event) => {
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('[data-custom-filter-page]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const resultsList = this.closest('results-list');

    if (!(resultsList instanceof HTMLElement) || !resultsList.contains(link)) return;

    const page = Number.parseInt(link.dataset.customFilterPage || '1', 10);

    if (!Number.isFinite(page) || page < 1) return;

    event.preventDefault();
    this.#setFilteredPage(page);
  };

  #normalizeState(state = {}) {
    return {
      keyword: (state.keyword || '').trim().toLowerCase(),
      priceType: state.priceType || 'all',
      collaborations: Array.isArray(state.collaborations) ? state.collaborations : [],
      categories: Array.isArray(state.categories) ? state.categories : [],
      featuredArtists: Array.isArray(state.featuredArtists) ? state.featuredArtists : [],
      series: Array.isArray(state.series) ? state.series : [],
      characters: Array.isArray(state.characters) ? state.characters : [],
      sizes: Array.isArray(state.sizes) ? state.sizes : [],
      colors: Array.isArray(state.colors) ? state.colors : [],
      sexes: this.shouldHideSexFilter
        ? []
        : Array.isArray(state.sexes)
          ? state.sexes.filter((sexHandle) => SEX_OPTION_ORDER.includes(sexHandle))
          : [],
    };
  }

  #removeHiddenSexFilterFromUrl() {
    if (!this.shouldHideSexFilter) return;

    const url = new URL(window.location.href);

    if (!url.searchParams.has(SEX_PARAM)) return;

    url.searchParams.delete(SEX_PARAM);
    history.replaceState({}, '', url.toString());
  }

  #getFilteredCards(cards, normalizedState) {
    return cards.filter((card) => {
      const matchesKeyword = !normalizedState.keyword || card.keywords.includes(normalizedState.keyword);
      const matchesPriceType = normalizedState.priceType === 'all' || card.priceType === normalizedState.priceType;
      const matchesCollaboration =
        !normalizedState.collaborations.length ||
        card.collaborations.some((collaboration) => normalizedState.collaborations.includes(collaboration.handle));
      const matchesCategories =
        !normalizedState.categories.length ||
        card.categories.some((category) => normalizedState.categories.includes(category.handle));
      const matchesFeaturedArtist =
        !normalizedState.featuredArtists.length ||
        card.featuredArtists.some((artist) => normalizedState.featuredArtists.includes(artist.handle));
      const matchesSeries =
        !normalizedState.series.length || card.series.some((item) => normalizedState.series.includes(item.handle));
      const matchesCharacters =
        !normalizedState.characters.length ||
        card.characters.some((item) => normalizedState.characters.includes(item.handle));
      const matchesSizes =
        !normalizedState.sizes.length || card.sizes.some((item) => normalizedState.sizes.includes(item.handle));
      const matchesColors =
        !normalizedState.colors.length || card.colors.some((color) => normalizedState.colors.includes(color.handle));
      const matchesSexes =
        !normalizedState.sexes.length || card.sexes.some((sex) => normalizedState.sexes.includes(sex.handle));

      return (
        matchesKeyword &&
        matchesPriceType &&
        matchesCollaboration &&
        matchesCategories &&
        matchesFeaturedArtist &&
        matchesSeries &&
        matchesCharacters &&
        matchesSizes &&
        matchesColors &&
        matchesSexes
      );
    });
  }

  async #applyFilters(state) {
    const requestId = ++this.#requestId;
    const normalizedState = this.#normalizeState(state);
    let stateKey = this.#getFilterStateKey(normalizedState);

    const grid = this.#getGrid();

    if (!grid) return;

    /*
     * Dynamic filter options must be recalculated every time the applied filter
     * state changes. Previously #optionCollections was only populated on the
     * first hydration, so an option that disappeared because of a Character,
     * Series, Category, etc. filter could stay missing after that filter was
     * removed.
     */
    if (!this.#isActiveState(normalizedState)) {
      this.#restoreBaseGrid();
      this.#resetFilteredState();
      this.#broadcastResults({
        count: this.#getBaseCount(),
        loading: false,
      });

      await this.#nextFrame();

      if (requestId !== this.#requestId) return;

      try {
        const cards = await this.#getAllCards();

        if (requestId !== this.#requestId) return;

        // Restore every contextual option from the complete, unfiltered product set.
        this.#primeOptionCollections(cards, normalizedState);
        this.#broadcastAllOptions();
      } catch {
        // Product rendering is already restored above. A failed option refresh
        // should not prevent the collection grid from remaining usable.
      }

      return;
    }

    if (!this.#isCustomFilteringActive) {
      this.#captureBaseState();
    }

    this.#broadcastResults({
      count: this.#getBaseCount(),
      loading: true,
    });

    await this.#nextFrame();

    if (requestId !== this.#requestId) return;

    const cards = await this.#getAllCards();

    if (requestId !== this.#requestId) return;

    // Always rebuild contextual filter options from the latest state.
    this.#primeOptionCollections(cards, normalizedState);

    if (normalizedState.sexes.length) {
      const availableSexHandles = new Set((this.#optionCollections.sex || []).map((option) => option.handle));
      const validSexes = normalizedState.sexes.filter((sexHandle) => availableSexHandles.has(sexHandle));

      if (validSexes.length !== normalizedState.sexes.length) {
        normalizedState.sexes = validSexes;
        updateFilterStateInUrl(normalizedState);
        stateKey = this.#getFilterStateKey(normalizedState);

        // Removing an unavailable Sex selection changes the context for the
        // other option groups too, so calculate them again before broadcasting.
        this.#primeOptionCollections(cards, normalizedState);
      }
    }

    // Push the refreshed option lists to every rendered filter form (desktop/mobile).
    this.#broadcastAllOptions();

    const isNewFilteredState = this.#filteredStateKey !== stateKey;

    if (isNewFilteredState) {
      this.#filteredCards = this.#getFilteredCards(cards, normalizedState);
      this.#filteredStateKey = stateKey;
    }

    const totalPages = this.#getTotalPages(this.#filteredCards.length);
    const requestedPage = isNewFilteredState ? 1 : getCurrentPageFromUrl();
    const currentPage = this.#clampPage(requestedPage, totalPages);

    if (currentPage !== getCurrentPageFromUrl()) {
      updateActiveFilterPageInUrl(currentPage, { replace: true });
    }

    this.#renderFilteredPage(this.#filteredCards, currentPage);
    this.#isCustomFilteringActive = true;

    this.#broadcastResults({
      count: this.#filteredCards.length,
      loading: false,
    });
  }

  #captureBaseState() {
    const grid = this.#getGrid();

    if (!grid) return;

    this.#baseGridHTML = grid.innerHTML;
    this.#baseLastPage = grid.dataset.lastPage || '1';
    this.#baseUrlKey = this.#getBaseUrl().toString();
    this.#allCards = null;
    this.#allCardsPromise = null;
    this.#filteredCards = [];
    this.#filteredCurrentPage = 1;
    this.#filteredStateKey = '';
    this.#idlePrefetchScheduled = false;
    this.#optionCollectionsHydrated = false;
    this.#optionCollections = {
      collaboration: [],
      category: [],
      featuredArtist: [],
      series: [],
      character: [],
      size: [],
      color: [],
      sex: [],
    };
  }

  #restoreBaseGrid() {
    const grid = this.#getGrid();

    if (!grid) return;

    grid.innerHTML = this.#baseGridHTML;
    grid.dataset.lastPage = this.#baseLastPage;
    this.#syncRenderedGrid(grid);

    this.#setResultsListFilteringState(false);
    this.#togglePagination(true);
    this.#hideFilteredPagination();
    this.#isCustomFilteringActive = false;
  }

  async #getAllCards() {
    const baseUrl = this.#getBaseUrl();
    const baseUrlKey = baseUrl.toString();

    if (this.#allCards && this.#baseUrlKey === baseUrlKey) {
      return this.#allCards;
    }

    if (this.#allCardsPromise && this.#baseUrlKey === baseUrlKey) {
      return this.#allCardsPromise;
    }

    const lastPage = Math.max(Number(this.#baseLastPage || 1), 1);

    if (lastPage === 1) {
      const visibleCards = this.#getVisibleCards();
      this.#allCards = visibleCards;
      this.#baseUrlKey = baseUrlKey;
      return visibleCards;
    }

    const pageRequests = Array.from({ length: lastPage }, (_, index) => {
      const page = index + 1;
      const pageUrl = new URL(baseUrl.toString());
      pageUrl.searchParams.set('page', page.toString());

      return this.#fetchCardsForPage(pageUrl);
    });

    this.#baseUrlKey = baseUrlKey;
    this.#allCardsPromise = Promise.all(pageRequests)
      .then((results) => {
        const cards = results.flat();
        this.#allCards = cards;

        return cards;
      })
      .finally(() => {
        if (this.#baseUrlKey === baseUrlKey) {
          this.#allCardsPromise = null;
        }
      });

    return this.#allCardsPromise;
  }

  async #fetchCardsForPage(url) {
    const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsedDocument.querySelector('[ref="grid"]');

    if (!grid) return [];

    return this.#extractCardsFromGrid(grid);
  }

  #getBaseUrl() {
    const url = new URL(window.location.href);

    url.searchParams.delete('page');
    url.searchParams.delete(KEYWORD_PARAM);
    url.searchParams.delete(PRICE_TYPE_PARAM);
    url.searchParams.delete(COLLABORATION_PARAM);
    url.searchParams.delete(CATEGORY_PARAM);
    url.searchParams.delete(FEATURED_ARTIST_PARAM);
    url.searchParams.delete(SERIES_PARAM);
    url.searchParams.delete(CHARACTER_PARAM);
    url.searchParams.delete(SIZE_PARAM);
    url.searchParams.delete(COLOR_PARAM);
    url.searchParams.delete(SEX_PARAM);
    url.hash = '';

    return url;
  }

  #getGrid() {
    return this.closest('results-list')?.querySelector('[ref="grid"]');
  }

  #getVisibleCards() {
    const grid = this.#getGrid();

    if (!grid) return [];

    return this.#extractCardsFromGrid(grid);
  }

  #getBaseCount() {
    return Number(this.getAttribute('results-count') || 0);
  }

  #scheduleIdlePrefetch() {
    if (this.#idlePrefetchScheduled || !this.#shouldPreloadAllCards()) return;

    this.#idlePrefetchScheduled = true;

    requestIdleCallback(async () => {
      try {
        const cards = await this.#getAllCards();

        if (!this.#optionCollectionsHydrated) {
          this.#primeOptionCollections(cards);
          this.#broadcastAllOptions();
        }
      } catch {
        this.#idlePrefetchScheduled = false;
      }
    });
  }

  #shouldPreloadAllCards() {
    const lastPage = Math.max(Number(this.#baseLastPage || 1), 1);

    if (lastPage <= 1) return false;

    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    if (connection?.saveData) return false;

    const effectiveType = connection?.effectiveType || '';

    if (effectiveType === '2g' || effectiveType === 'slow-2g') return false;

    return true;
  }

  #togglePagination(show) {
    const container = this.closest('results-list');

    if (!container) return;

    const shouldShow = show && this.#hasMultiplePages();

    container.querySelectorAll('[ref="viewMorePrevious"], [ref="viewMoreNext"], .pagination').forEach((element) => {
      if (element instanceof HTMLElement) {
        element.hidden = !shouldShow;
      }
    });
  }

  #hasMultiplePages() {
    const grid = this.#getGrid();
    const lastPage = grid?.dataset.lastPage || this.#baseLastPage || '1';

    return Math.max(Number(lastPage || 1), 1) > 1;
  }

  #ensureBasePageStored() {
    try {
      const storedPage = sessionStorage.getItem(BASE_PAGE_SESSION_KEY) || '';
      const currentPage = getCurrentPageFromUrl();

      if (!storedPage && currentPage > 1) {
        sessionStorage.setItem(BASE_PAGE_SESSION_KEY, String(currentPage));
      }
    } catch {}
  }

  #getTotalPages(count) {
    return Math.max(Math.ceil(count / this.pageSize), 1);
  }

  #clampPage(page, totalPages) {
    return Math.min(Math.max(page, 1), Math.max(totalPages, 1));
  }

  #getFilterStateKey(state) {
    return JSON.stringify({
      keyword: state.keyword,
      priceType: state.priceType,
      collaborations: [...state.collaborations].sort(),
      categories: [...state.categories].sort(),
      featuredArtists: [...state.featuredArtists].sort(),
      series: [...state.series].sort(),
      characters: [...state.characters].sort(),
      sizes: [...state.sizes].sort(),
      colors: [...state.colors].sort(),
      sexes: [...state.sexes].sort(),
    });
  }

  #resetFilteredState() {
    this.#filteredCards = [];
    this.#filteredCurrentPage = 1;
    this.#filteredStateKey = '';
    this.#hideFilteredPagination();
  }

  #renderFilteredPage(cards, page) {
    const grid = this.#getGrid();

    if (!grid) return;

    const totalPages = this.#getTotalPages(cards.length);
    const currentPage = this.#clampPage(page, totalPages);
    const start = (currentPage - 1) * this.pageSize;
    const pageCards = cards.slice(start, start + this.pageSize);

    grid.innerHTML = pageCards.length ? pageCards.map((card) => card.html).join('') : this.#renderEmptyState();
    grid.dataset.lastPage = String(totalPages);
    this.#syncRenderedGrid(grid);

    if (pageCards.length) {
      grid.querySelectorAll(':scope > [ref="cards[]"]').forEach((card) => {
        if (card instanceof HTMLElement) {
          card.dataset.page = String(currentPage);
        }
      });
    }

    this.#setResultsListFilteringState(true);
    this.#togglePagination(false);
    this.#renderFilteredPagination(totalPages, currentPage);
    this.#filteredCurrentPage = currentPage;
  }

  #setFilteredPage(page) {
    if (!this.#isCustomFilteringActive) return;

    const totalPages = this.#getTotalPages(this.#filteredCards.length);
    const nextPage = this.#clampPage(page, totalPages);

    if (nextPage === this.#filteredCurrentPage) return;

    updateActiveFilterPageInUrl(nextPage);
    this.#renderFilteredPage(this.#filteredCards, nextPage);
  }

  #renderFilteredPagination(totalPages, currentPage) {
    const pagination = this.#getFilteredPaginationElement();
    if (!(pagination instanceof HTMLElement)) return;

    if (totalPages <= 1) {
      pagination.hidden = true;
      pagination.innerHTML = '';
      return;
    }

    const parts = this.#getPaginationParts(totalPages, currentPage);
    const previousPage = currentPage - 1;
    const nextPage = currentPage + 1;

    pagination.hidden = false;
    pagination.dataset.currentPage = String(currentPage);
    pagination.innerHTML = `
      <ul class="pagination__list" role="list">
        <li class="pagination__item">
          ${
            previousPage >= 1
              ? this.#renderPaginationLink(previousPage, FILTERED_PAGINATION_PREVIOUS_LABEL, 'pagination__link pagination__link--arrow', FILTERED_PAGINATION_PREVIOUS_ICON)
              : `<span aria-label="${FILTERED_PAGINATION_PREVIOUS_LABEL}" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">${FILTERED_PAGINATION_PREVIOUS_ICON}</span>`
          }
        </li>
        ${parts
          .map((part) => {
            if (part === 'ellipsis') {
              return `
                <li class="pagination__item pagination__item--gap">
                  <span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>
                </li>
              `;
            }

            if (part === currentPage) {
              return `
                <li class="pagination__item">
                  <span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${part}</span>
                </li>
              `;
            }

            return `
              <li class="pagination__item">
                ${this.#renderPaginationLink(part, `Page ${part}`, 'pagination__link pagination__link--page', String(part))}
              </li>
            `;
          })
          .join('')}
        <li class="pagination__item">
          ${
            nextPage <= totalPages
              ? this.#renderPaginationLink(nextPage, FILTERED_PAGINATION_NEXT_LABEL, 'pagination__link pagination__link--arrow', FILTERED_PAGINATION_NEXT_ICON)
              : `<span aria-label="${FILTERED_PAGINATION_NEXT_LABEL}" class="pagination__link pagination__link--arrow pagination__link--disabled" aria-disabled="true">${FILTERED_PAGINATION_NEXT_ICON}</span>`
          }
        </li>
      </ul>
    `;
  }

  #renderPaginationLink(page, label, className, content) {
    const url = this.#getFilteredPageUrl(page);

    return `
      <a
        href="${url.toString()}"
        aria-label="${label}"
        class="${className}"
        data-custom-filter-page="${page}"
      >
        ${content}
      </a>
    `;
  }

  #getPaginationParts(totalPages, currentPage) {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages]);

    if (currentPage <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    } else if (currentPage >= totalPages - 2) {
      pages.add(totalPages - 1);
      pages.add(totalPages - 2);
      pages.add(totalPages - 3);
    } else {
      pages.add(currentPage - 1);
      pages.add(currentPage);
      pages.add(currentPage + 1);
    }

    const orderedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const parts = [];

    orderedPages.forEach((page, index) => {
      if (index > 0 && page - orderedPages[index - 1] > 1) {
        parts.push('ellipsis');
      }

      parts.push(page);
    });

    return parts;
  }

  #getFilteredPageUrl(page) {
    const url = new URL(window.location.href);

    if (page > 1) {
      url.searchParams.set(PAGE_PARAM, String(page));
    } else {
      url.searchParams.delete(PAGE_PARAM);
    }

    return url;
  }

  #getFilteredPaginationElement() {
    const resultsList = this.closest('results-list');
    const grid = this.#getGrid();

    if (!(resultsList instanceof HTMLElement) || !(grid instanceof HTMLElement)) return null;

    let pagination = resultsList.querySelector('[data-custom-filter-pagination]');

    if (!(pagination instanceof HTMLElement)) {
      pagination = document.createElement('nav');
      pagination.className = 'custom-collection-filters-pagination';
      pagination.hidden = true;
      pagination.setAttribute('aria-label', FILTERED_PAGINATION_LABEL);
      pagination.setAttribute('data-custom-filter-pagination', '');
      grid.after(pagination);
    }

    return pagination;
  }

  #setResultsListFilteringState(isActive) {
    const resultsList = this.closest('results-list');
    if (!(resultsList instanceof HTMLElement)) return;

    resultsList.toggleAttribute('data-custom-filtering-active', Boolean(isActive));
  }

  #hideFilteredPagination() {
    const resultsList = this.closest('results-list');
    if (!(resultsList instanceof HTMLElement)) return;

    const pagination = resultsList.querySelector('[data-custom-filter-pagination]');
    if (!(pagination instanceof HTMLElement)) return;

    pagination.hidden = true;
    pagination.innerHTML = '';
  }

  #renderEmptyState() {
    return `
      <li class="product-grid__item custom-collection-filters__empty" ref="cards[]" data-page="1">
        No products match your filters.
      </li>
    `;
  }

  #syncRenderedGrid(grid) {
    if (!(grid instanceof HTMLElement)) return;

    if (window.customElements?.upgrade) {
      window.customElements.upgrade(grid);
    }

    grid.dispatchEvent(
      new CustomEvent('model-image-switcher:refresh', {
        bubbles: true,
      })
    );

    document.dispatchEvent(
      new CustomEvent('collection:updated', {
        detail: {
          sectionId: this.sectionId,
        },
      })
    );
  }

  #extractCardsFromGrid(grid) {
    return Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')).map((card) => {
      const keywords = (card.getAttribute('data-custom-filter-keywords') || '').toLowerCase();
      const categories = this.#parseCollaborations(card.getAttribute('data-custom-categories') || '');
      const isAccessory = this.#isAccessoryCard(categories, keywords);
      const parsedSexes = this.#parseCollaborations(card.getAttribute('data-custom-sex') || '').filter((sex) =>
        SEX_OPTION_ORDER.includes(sex.handle)
      );
      const sexes = isAccessory ? [] : this.#normalizeCardSexes(parsedSexes);

      return {
        html: card.outerHTML,
        keywords,
        priceType: card.getAttribute('data-custom-price-type') || 'regular',
        collaborations: this.#parseCollaborations(card.getAttribute('data-custom-collaborations') || ''),
        categories,
        featuredArtists: this.#parseCollaborations(card.getAttribute('data-custom-featured-artists') || ''),
        series: this.#parseCollaborations(card.getAttribute('data-custom-series') || ''),
        characters: this.#parseCollaborations(card.getAttribute('data-custom-characters') || ''),
        sizes: this.#parseCollaborations(card.getAttribute('data-custom-sizes') || ''),
        colors: this.#parseColors(card.getAttribute('data-custom-colors') || ''),
        sexes,
      };
    });
  }

  #normalizeCardSexes(sexes = []) {
    const sexByHandle = new Map(
      sexes
        .filter((sex) => sex && SEX_OPTION_ORDER.includes(sex.handle))
        .map((sex) => [sex.handle, sex])
    );

    // Strict classification: if stale/mixed markup contains both UNISEX and WOMEN,
    // treat the product as UNISEX so it cannot appear in the Women-only filter.
    const primaryHandle = SEX_OPTION_ORDER.find((handle) => sexByHandle.has(handle));

    return primaryHandle ? [sexByHandle.get(primaryHandle)] : [];
  }

  #isAccessoryCard(categories = [], keywords = '') {
    const hasAccessoryCategory = categories.some((category) => {
      const handle = (category.handle || '').trim().toLowerCase();
      const title = (category.title || '').trim().toLowerCase();

      return handle === 'accessory' || handle === 'accessories' || title === 'accessory' || title === 'accessories';
    });

    if (hasAccessoryCategory) return true;

    const normalizedKeywords = ` ${keywords
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()} `;

    return normalizedKeywords.includes(' accessory ') || normalizedKeywords.includes(' accessories ');
  }

  #broadcastResults({ count, loading }) {
    document.dispatchEvent(
      new CustomEvent(FILTER_RESULTS_EVENT, {
        detail: {
          count,
          loading,
        },
      })
    );
  }

  #isActiveState(state) {
    return (
      Boolean((state.keyword || '').trim()) ||
      (state.priceType || 'all') !== 'all' ||
      Boolean(state.collaborations?.length) ||
      Boolean(state.categories?.length) ||
      Boolean(state.featuredArtists?.length) ||
      Boolean(state.series?.length) ||
      Boolean(state.characters?.length) ||
      Boolean(state.sizes?.length) ||
      Boolean(state.colors?.length) ||
      Boolean(state.sexes?.length)
    );
  }

  #parseCollaborations(value) {
    if (!value) return [];

    return value
      .split('||')
      .map((item) => {
        const [handle, ...titleParts] = item.split('::');
        const title = titleParts.join('::');

        if (!handle || !title) return null;

        return {
          handle,
          title,
        };
      })
      .filter(Boolean);
  }

  #parseColors(value) {
    if (!value) return [];

    return value
      .split('||')
      .map((item) => {
        const [handle, title, swatch] = item.split('::');

        if (!handle || !title) return null;

        return {
          handle,
          title,
          swatch: swatch || handle,
        };
      })
      .filter(Boolean);
  }

  #getOptionsFromCards(cards, key) {
    const options = new Map();

    cards.forEach((card) => {
      (card[key] || []).forEach((option) => {
        if (!options.has(option.handle)) {
          options.set(option.handle, option);
        }
      });
    });

    const normalizedOptions = Array.from(options.values());

    if (key === 'sexes') {
      return SEX_OPTION_ORDER.filter((handle) => options.has(handle)).map((handle) => ({
        handle,
        title: options.get(handle)?.title || SEX_OPTION_TITLES[handle],
      }));
    }

    if (key === 'characters') {
      return normalizedOptions.sort((a, b) => {
        const aIndex = CHARACTER_OPTION_ORDER.indexOf((a.title || '').trim().toLowerCase());
        const bIndex = CHARACTER_OPTION_ORDER.indexOf((b.title || '').trim().toLowerCase());
        const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

        if (normalizedAIndex !== normalizedBIndex) {
          return normalizedAIndex - normalizedBIndex;
        }

        return a.title.localeCompare(b.title);
      });
    }

    if (key === 'sizes') {
      return normalizedOptions.sort((a, b) => {
        const normalizedATitle = (a.title || '').trim().toLowerCase();
        const normalizedBTitle = (b.title || '').trim().toLowerCase();
        const aIndex = SIZE_OPTION_ORDER.indexOf(a.handle || normalizedATitle);
        const bIndex = SIZE_OPTION_ORDER.indexOf(b.handle || normalizedBTitle);
        const normalizedAIndex = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedBIndex = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;

        if (normalizedAIndex !== normalizedBIndex) {
          return normalizedAIndex - normalizedBIndex;
        }

        const aNumber = Number.parseFloat(normalizedATitle);
        const bNumber = Number.parseFloat(normalizedBTitle);
        const aIsNumber = Number.isFinite(aNumber) && String(aNumber) === normalizedATitle;
        const bIsNumber = Number.isFinite(bNumber) && String(bNumber) === normalizedBTitle;

        if (aIsNumber && bIsNumber && aNumber !== bNumber) {
          return aNumber - bNumber;
        }

        return a.title.localeCompare(b.title, undefined, { numeric: true });
      });
    }

    return normalizedOptions.sort((a, b) => a.title.localeCompare(b.title));
  }

  /**
   * @param {keyof typeof OPTION_FILTERS} type
   * @param {object[]} cards
   * @param {object} [normalizedState]
   */
  #getContextualOptions(type, cards, normalizedState = this.#normalizeState(getFilterStateFromUrl())) {
    const optionFilter = OPTION_FILTERS[type];

    if (!optionFilter) return [];
    if (type === 'sex' && this.shouldHideSexFilter) return [];

    const contextState = { ...normalizedState, [optionFilter.stateKey]: [] };
    const contextualCards = this.#getFilteredCards(cards, contextState);

    return this.#getOptionsFromCards(contextualCards, optionFilter.cardKey);
  }

  #primeOptionCollections(cards, normalizedState = this.#normalizeState(getFilterStateFromUrl())) {
    this.#optionCollections.collaboration = this.#getContextualOptions('collaboration', cards, normalizedState);
    this.#optionCollections.category = this.#getContextualOptions('category', cards, normalizedState);
    this.#optionCollections.featuredArtist = this.#getContextualOptions('featuredArtist', cards, normalizedState);
    this.#optionCollections.series = this.#getContextualOptions('series', cards, normalizedState);
    this.#optionCollections.character = this.#getContextualOptions('character', cards, normalizedState);
    this.#optionCollections.size = this.#getContextualOptions('size', cards, normalizedState);
    this.#optionCollections.color = this.#getContextualOptions('color', cards, normalizedState);
    this.#optionCollections.sex = this.#getContextualOptions('sex', cards, normalizedState);
    this.#optionCollectionsHydrated = true;
  }

  #broadcastAllOptions() {
    this.#broadcastOptions('collaboration', this.#optionCollections.collaboration);
    this.#broadcastOptions('category', this.#optionCollections.category);
    this.#broadcastOptions('featuredArtist', this.#optionCollections.featuredArtist);
    this.#broadcastOptions('series', this.#optionCollections.series);
    this.#broadcastOptions('character', this.#optionCollections.character);
    this.#broadcastOptions('size', this.#optionCollections.size);
    this.#broadcastOptions('color', this.#optionCollections.color);
    this.#broadcastOptions('sex', this.#optionCollections.sex);
  }

  async #handleOptionRequest(type) {
    const optionFilter = OPTION_FILTERS[type];

    if (!optionFilter) return;

    if (type === 'sex' && this.shouldHideSexFilter) {
      this.#optionCollections.sex = [];
      this.#broadcastOptions('sex', []);
      return;
    }

    const normalizedState = this.#normalizeState(getFilterStateFromUrl());

    if (!this.#optionCollectionsHydrated) {
      const visibleCards = this.#getVisibleCards();

      if (visibleCards.length) {
        this.#broadcastOptions(type, this.#getContextualOptions(type, visibleCards ?? [], normalizedState));
        await this.#nextFrame();
      }
    }

    const cards = await this.#getAllCards();

    if (!this.#optionCollectionsHydrated) {
      this.#primeOptionCollections(cards, normalizedState);
    } else {
      this.#optionCollections[type] = this.#getContextualOptions(type, cards, normalizedState);
    }

    this.#broadcastOptions(type, this.#optionCollections[type] || []);
  }

  #broadcastOptions(type, options) {
    const optionFilter = OPTION_FILTERS[type];

    if (!optionFilter) return;

    document.dispatchEvent(
      new CustomEvent(optionFilter.optionsEvent, {
        detail: {
          options,
        },
      })
    );
  }

  #nextFrame() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => resolve());
    });
  }
}

if (!customElements.get('custom-collection-filters-form')) {
  customElements.define('custom-collection-filters-form', CustomCollectionFiltersForm);
}

if (!customElements.get('custom-collection-filters-controller')) {
  customElements.define('custom-collection-filters-controller', CustomCollectionFiltersController);
}

/**
 * Custom filter popup stacking guard.
 * The theme's dialog-component can leave the previous custom popup open when another trigger is clicked.
 * Close every other custom filter popup first, so only one popup is visible at a time.
 */
(() => {
  if (!CUSTOM_FILTER_MODAL_TRIGGER_SELECTOR) return;

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const trigger = target.closest(CUSTOM_FILTER_MODAL_TRIGGER_SELECTOR);
      if (!(trigger instanceof Element)) return;

      const currentDialog = getCustomCollectionFilterDialogFromTrigger(trigger);
      closeCustomCollectionFilterDialogs({ exceptDialog: currentDialog });

      window.setTimeout(syncCustomCollectionFilterModalState, 0);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const closeButton = target.closest('.custom-collection-filters__modal-close, .custom-collection-filters__modal-select');
    if (!(closeButton instanceof HTMLElement)) return;

    const dialog = closeButton.closest('dialog.custom-collection-filters__modal');
    if (dialog instanceof HTMLDialogElement) {
      window.setTimeout(() => closeCustomCollectionFilterDialog(dialog), 0);
    }
  });

  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches(CUSTOM_FILTER_DIALOG_SELECTOR)) return;

      window.setTimeout(syncCustomCollectionFilterModalState, 0);
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog.matches(CUSTOM_FILTER_DIALOG_SELECTOR)) return;

      syncCustomCollectionFilterModalState();
    },
    true
  );
})();

/**
 * Mobile filter drawer close on "See xxx items"
 *
 * Behavior:
 * - Mobile only.
 * - Does NOT close when selecting checkbox/radio filters.
 * - Closes the main filter drawer only after clicking:
 *   1. Custom drawer button: "See Items (x)"
 *   2. Custom popup button: "See x items"
 *   3. Native drawer button: "See x items"
 */
(() => {
  const MOBILE_QUERY = '(max-width: 749px)';

  const SEE_ITEMS_BUTTON_SELECTOR = [
    '.custom-collection-filters__see-items',
    '.custom-collection-filters__modal-select',
    '.facets__see-results',
    '[ref="seeItemsButton"]',
  ].join(',');

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getMainFiltersDrawer(button) {
    const drawerComponent = button.closest('#filters-drawer') || document.querySelector('#filters-drawer');

    const drawerDialog =
      drawerComponent?.querySelector('dialog.facets--drawer, dialog.dialog-drawer, dialog.drawer') ||
      document.querySelector('#filters-drawer dialog[open]') ||
      document.querySelector('dialog.facets--drawer[open]');

    return {
      component: drawerComponent instanceof HTMLElement ? drawerComponent : null,
      dialog: drawerDialog instanceof HTMLElement ? drawerDialog : null,
    };
  }

  function closeMainFiltersDrawer(button) {
    const { component, dialog } = getMainFiltersDrawer(button);

    const closeButton =
      component?.querySelector('.facets-drawer__close') ||
      dialog?.querySelector('.facets-drawer__close') ||
      component?.querySelector('[aria-label="Close"]') ||
      dialog?.querySelector('[aria-label="Close"]');

    /**
     * Best option: click the theme's real close button.
     * This lets Shopify/theme clean up scroll lock, focus trap, and drawer state.
     */
    if (closeButton instanceof HTMLElement) {
      closeButton.click();
    }

    /**
     * Fallback cleanup in case the theme close handler does not run.
     */
    window.setTimeout(() => {
      if (dialog instanceof HTMLDialogElement && dialog.open) {
        dialog.close();
      }

      if (dialog instanceof HTMLElement) {
        dialog.removeAttribute('open');
        dialog.classList.remove('is-open', 'active', 'menu-opening');
      }

      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }, 150);
  }

  document.addEventListener('click', (event) => {
    if (!isMobile()) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(SEE_ITEMS_BUTTON_SELECTOR);
    if (!(button instanceof HTMLButtonElement)) return;

    /**
     * Let your existing filter logic run first.
     * Then close the drawer.
     */
    window.setTimeout(() => {
      closeMainFiltersDrawer(button);
    }, 180);
  });
})();

/**
 * Custom mobile filter drawer
 *
 * Opens the isolated custom filter sidebar from the mobile toolbar Filter button.
 * This is separate from Shopify's native facets drawer so the custom filter does
 * not render inline on mobile and does not conflict with the native facet form.
 */
(() => {
  const MOBILE_QUERY = '(max-width: 749px)';
  const OPEN_SELECTOR = '[data-custom-mobile-filter-open]';
  const CLOSE_SELECTOR = '[data-custom-mobile-filter-close]';
  const DRAWER_SELECTOR = '[data-custom-mobile-filter-drawer]';
  const DRAWER_OPEN_CLASS = 'custom-mobile-filter-drawer-open';

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function getDrawer(trigger) {
    const controls = trigger?.getAttribute?.('aria-controls');
    const controlledDrawer = controls ? document.getElementById(controls) : null;

    if (controlledDrawer instanceof HTMLElement && controlledDrawer.matches(DRAWER_SELECTOR)) {
      return controlledDrawer;
    }

    return document.querySelector(DRAWER_SELECTOR);
  }

  function openDrawer(drawer) {
    if (!(drawer instanceof HTMLElement)) return;

    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add(DRAWER_OPEN_CLASS);

    requestAnimationFrame(() => {
      drawer.classList.add('is-open');

      const firstFocusable = drawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (firstFocusable instanceof HTMLElement) {
        firstFocusable.focus({ preventScroll: true });
      }
    });
  }

  function closeDrawer(drawer) {
    if (!(drawer instanceof HTMLElement) || drawer.hidden) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove(DRAWER_OPEN_CLASS);

    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
    }, 230);
  }

  function closeAllDrawers() {
    document.querySelectorAll(DRAWER_SELECTOR).forEach((drawer) => {
      if (drawer instanceof HTMLElement) closeDrawer(drawer);
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openButton = target.closest(OPEN_SELECTOR);
    if (openButton instanceof HTMLElement) {
      event.preventDefault();
      if (!isMobile()) return;
      openDrawer(getDrawer(openButton));
      return;
    }

    const closeButton = target.closest(CLOSE_SELECTOR);
    if (closeButton instanceof HTMLElement) {
      event.preventDefault();
      closeDrawer(closeButton.closest(DRAWER_SELECTOR));
      return;
    }

    const seeItemsButton = target.closest('.custom-collection-filters__see-items, [ref="seeItemsButton"]');
    if (seeItemsButton instanceof HTMLButtonElement) {
      const drawer = seeItemsButton.closest(DRAWER_SELECTOR);
      if (drawer instanceof HTMLElement) {
        window.setTimeout(() => closeDrawer(drawer), 180);
      }
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllDrawers();
  });

  window.addEventListener('resize', () => {
    if (!isMobile()) closeAllDrawers();
  });
})();
